import {
  CorporateFacilitySubscription,
  CorporateSubscriptionInvoiceGroup,
  CorporateSubscriptionInvoiceGroupDetail,
  FacilitySubscriptionProduct,
  FacilitySubscriptionState,
  FacilitySubscriptionStatus,
  InvoiceGroupStatus,
  InvoiceGroupType,
  InvoiceStatus,
  OpenCycleSubscription,
  SubscriptionInvoice,
  SubscriptionInvoiceLineType,
  SubscriptionInvoiceShift,
  SubscriptionTier,
  SubscriptionTierBand,
  SUBSCRIPTION_TIER_BANDS
} from '../models';

/** The ladder the API serves; identical to the fallback the pricing table uses. */
export const MOCK_SUBSCRIPTION_TIER_BANDS: readonly SubscriptionTierBand[] =
  SUBSCRIPTION_TIER_BANDS;

/** Product states from the Figma designs. */
export const MOCK_SUBSCRIPTION_STATUSES: FacilitySubscriptionStatus[] = [
  {
    product: FacilitySubscriptionProduct.TemporaryStaffing,
    // Inactive so the dashboard profile setup checklist is reachable; the real
    // endpoint decides this. Flip to true to see the subscribed state.
    active: false
    // No monthlyPrice — priced from the shift tiers rather than a flat rate.
  },
  {
    product: FacilitySubscriptionProduct.JobPostings,
    active: false,
    monthlyPrice: 100
  }
];

/** Reference usage from the Figma designs: 22 of 38 scheduled shifts worked. */
export const MOCK_OPEN_CYCLE: OpenCycleSubscription = {
  facilityId: 'fac-1',
  facilityName: 'Downtown Hospital',
  cyclePeriodStart: '2026-07-01',
  cyclePeriodEnd: '2026-07-31',
  cyclePeriodFormatted: '1–31 July 2026',
  daysRemaining: 12,
  plannedShiftCount: 38,
  actualShiftCount: 22,
  currentTier: SubscriptionTier.Tier1,
  currentTierDisplay: 'Tier 1',
  currentTierShiftRange: '1-25',
  currentTierCost: 200,
  projectedTier: SubscriptionTier.Tier2,
  projectedTierDisplay: 'Tier 2',
  projectedTierShiftRange: '26-50',
  projectedTierCost: 500,
  estimatedProductFees: 0,
  estimatedTotalIfCycleClosedNow: 200,
  estimatedTotalAtPlannedVolume: 500,
  isEstimate: true,
  bands: MOCK_SUBSCRIPTION_TIER_BANDS
};

/** Subscription invoices for the invoices tab. */
export const MOCK_SUBSCRIPTION_INVOICES: SubscriptionInvoice[] = [
  {
    id: 'a1000000-0000-4000-8000-000000000013',
    facilityId: 'fac-1',
    facilityName: 'Downtown Hospital',
    facilityTypeName: 'General Hospital',
    invoiceNumber: 'INV-2025-013',
    cyclePeriodStart: '2025-06-01',
    cyclePeriodEnd: '2025-06-30',
    cyclePeriodFormatted: '1–30 June 2025',
    generatedDate: '2025-06-30',
    dueDate: '2025-06-30',
    preTaxTotal: 11000,
    taxTotal: 540,
    totalAmount: 11540,
    paidAmount: 11540,
    remainingBalance: 0,
    status: InvoiceStatus.PAID,
    statusDisplay: 'Paid',
    isOverdue: false,
    daysOverdue: 0,
    countableShifts: 281,
    finalTierDisplay: 'Tier 4',
    finalTierShiftRange: '101+',
    tierPrice: 1000,
    billedTo: 'Facility',
    lines: []
  },
  {
    id: 'a1000000-0000-4000-8000-000000000014',
    facilityId: 'fac-1',
    facilityName: 'Downtown Hospital',
    facilityTypeName: 'General Hospital',
    invoiceNumber: 'INV-2025-014',
    cyclePeriodStart: '2025-07-01',
    cyclePeriodEnd: '2025-07-31',
    cyclePeriodFormatted: '1–31 July 2025',
    generatedDate: '2025-07-31',
    dueDate: '2025-07-31',
    preTaxTotal: 7750,
    taxTotal: 370,
    totalAmount: 8120,
    paidAmount: 8120,
    remainingBalance: 0,
    status: InvoiceStatus.PAID,
    statusDisplay: 'Paid',
    isOverdue: false,
    daysOverdue: 0,
    countableShifts: 192,
    finalTierDisplay: 'Tier 4',
    finalTierShiftRange: '101+',
    tierPrice: 1000,
    billedTo: 'Facility',
    lines: []
  },
  {
    id: 'a1000000-0000-4000-8000-000000000015',
    facilityId: 'fac-1',
    facilityName: 'Downtown Hospital',
    facilityTypeName: 'General Hospital',
    invoiceNumber: 'INV-2025-015',
    cyclePeriodStart: '2025-08-01',
    cyclePeriodEnd: '2025-08-31',
    cyclePeriodFormatted: '1–31 August 2025',
    generatedDate: '2025-08-31',
    dueDate: '2025-08-31',
    preTaxTotal: 750,
    taxTotal: 0,
    totalAmount: 750,
    paidAmount: 0,
    remainingBalance: 750,
    status: InvoiceStatus.OVERDUE,
    statusDisplay: 'Past due',
    isOverdue: true,
    daysOverdue: 14,
    suspensionDeadline: '2025-09-09',
    countableShifts: 64,
    finalTierDisplay: 'Tier 3',
    finalTierShiftRange: '51-100',
    tierPrice: 750,
    billedTo: 'Facility',
    lines: []
  },
  {
    id: 'a1000000-0000-4000-8000-000000000016',
    facilityId: 'fac-1',
    facilityName: 'Downtown Hospital',
    facilityTypeName: 'General Hospital',
    invoiceNumber: 'INV-2025-016',
    cyclePeriodStart: '2025-09-01',
    cyclePeriodEnd: '2025-09-30',
    cyclePeriodFormatted: '1–30 September 2025',
    generatedDate: '2025-09-30',
    dueDate: '2025-09-30',
    preTaxTotal: 400,
    taxTotal: 0,
    totalAmount: 400,
    paidAmount: 0,
    remainingBalance: 400,
    status: InvoiceStatus.SENT,
    statusDisplay: 'Invoiced',
    isOverdue: false,
    daysOverdue: 0,
    countableShifts: 23,
    finalTierDisplay: 'Tier 1',
    finalTierShiftRange: '1-25',
    tierPrice: 200,
    billedTo: 'Facility',
    lines: []
  }
];

/**
 * The shifts behind the detail invoice's tier charge.
 *
 * Deliberately mixed: worked shifts carry clock times, and the last is a
 * facility-initiated late cancellation — it counted toward the tier because the
 * booking was consumed, but nobody clocked in. The real list contains both.
 */
export const MOCK_SUBSCRIPTION_INVOICE_SHIFTS: SubscriptionInvoiceShift[] = [
  {
    shiftId: 'shift-1',
    shiftDate: '2025-06-05',
    contractorId: 'con-1',
    contractorName: 'Samuel Lee',
    clockInTime: '2025-06-05T08:00:00Z',
    clockOutTime: '2025-06-05T16:00:00Z',
    hoursWorked: 8,
    status: 'Completed',
    statusDisplay: 'Completed'
  },
  {
    shiftId: 'shift-2',
    shiftDate: '2025-06-05',
    contractorId: 'con-2',
    contractorName: 'Fatima Khan',
    clockInTime: '2025-06-05T09:00:00Z',
    clockOutTime: '2025-06-05T17:00:00Z',
    hoursWorked: 8,
    status: 'Completed',
    statusDisplay: 'Completed'
  },
  {
    shiftId: 'shift-3',
    shiftDate: '2025-06-06',
    contractorId: 'con-3',
    contractorName: 'David Smith',
    clockInTime: '2025-06-06T07:30:00Z',
    clockOutTime: '2025-06-06T15:30:00Z',
    hoursWorked: 8,
    status: 'Completed',
    statusDisplay: 'Completed'
  },
  {
    shiftId: 'shift-4',
    shiftDate: '2025-06-07',
    contractorId: 'con-4',
    contractorName: 'Emily Chen',
    clockInTime: '2025-06-07T10:00:00Z',
    clockOutTime: '2025-06-07T18:00:00Z',
    hoursWorked: 8,
    status: 'Completed',
    statusDisplay: 'Completed'
  },
  {
    // Counted, never worked — no clock times, no hours.
    shiftId: 'shift-5',
    shiftDate: '2025-06-09',
    contractorId: 'con-5',
    contractorName: 'Maria Gonzalez',
    status: 'Cancelled',
    statusDisplay: 'Late cancellation'
  }
];

/** The first invoice with its lines, as the detail route returns it. */
export const MOCK_SUBSCRIPTION_INVOICE_DETAIL: SubscriptionInvoice = {
  ...MOCK_SUBSCRIPTION_INVOICES[0],
  // Overridden so the detail reconciles with its own lines.
  countableShifts: 23,
  preTaxTotal: 403.2,
  taxTotal: 25.8,
  totalAmount: 429,
  paidAmount: 429,
  remainingBalance: 0,
  finalTierDisplay: 'Tier 1',
  finalTierShiftRange: '1-25',
  tierPrice: 200,
  lines: [
    {
      id: 'line-1',
      lineType: SubscriptionInvoiceLineType.TierCharge,
      lineTypeDisplay: 'Tier charge',
      description: 'Temporary staffing — Tier 1 (1-25 shifts)',
      usageDisplay: '23',
      preTaxAmount: 200,
      taxAmount: 12.9,
      totalAmount: 212.9
    },
    {
      id: 'line-2',
      lineType: SubscriptionInvoiceLineType.ProductFee,
      lineTypeDisplay: 'Product fee',
      description: 'Job Postings — monthly fee',
      usageDisplay: 'N/A',
      preTaxAmount: 100,
      taxAmount: 6.45,
      totalAmount: 106.45,
      product: FacilitySubscriptionProduct.JobPostings,
      productDisplay: 'Job Postings'
    },
    {
      id: 'line-3',
      lineType: SubscriptionInvoiceLineType.Proration,
      lineTypeDisplay: 'Proration',
      description: 'Job Postings — 15 of 31 days',
      usageDisplay: 'N/A',
      preTaxAmount: 100,
      taxAmount: 6.45,
      totalAmount: 106.45,
      product: FacilitySubscriptionProduct.JobPostings,
      productDisplay: 'Job Postings',
      proratedFrom: '2025-06-16',
      proratedTo: '2025-06-30'
    },
    {
      // Last, and with no product: the fee is charged on the payment as a
      // whole, not on anything above it. Its label comes from `description`,
      // which is the path any line without a product takes.
      id: 'line-4',
      lineType: SubscriptionInvoiceLineType.ProcessingFee,
      lineTypeDisplay: 'Processing fee',
      description: 'Payment processing fee',
      usageDisplay: 'N/A',
      preTaxAmount: 3.2,
      taxAmount: 0,
      totalAmount: 3.2
    }
  ]
};

/** Facilities under a corporation, as listed on the corporate rollup. */
export const MOCK_CORPORATE_FACILITY_SUBSCRIPTIONS: CorporateFacilitySubscription[] =
  [
    {
      facilityId: 'fac-1',
      facilityName: 'Sutter Health Hospital',
      status: FacilitySubscriptionState.Active,
      statusDisplay: 'Active',
      directStaffingActive: true,
      jobPostingsActive: true,
      currentTier: SubscriptionTier.Tier3,
      currentTierDisplay: 'Tier 3',
      tierShiftRange: '51-100',
      monthlyCost: 750,
      currentCycleStart: '2026-08-01',
      currentCycleEnd: '2026-08-31'
    },
    {
      facilityId: 'fac-2',
      facilityName: 'Cascade Senior Living',
      status: FacilitySubscriptionState.Active,
      statusDisplay: 'Active',
      directStaffingActive: true,
      jobPostingsActive: true,
      currentTier: SubscriptionTier.Tier3,
      currentTierDisplay: 'Tier 3',
      tierShiftRange: '51-100',
      monthlyCost: 750,
      currentCycleStart: '2026-08-01',
      currentCycleEnd: '2026-08-31'
    },
    {
      // Never subscribed — still listed, so the admin can see and subscribe it.
      facilityId: 'fac-3',
      facilityName: 'Mercy Wellness Center',
      statusDisplay: 'Not subscribed',
      directStaffingActive: false,
      jobPostingsActive: false,
      currentTier: SubscriptionTier.None,
      currentTierDisplay: 'None',
      tierShiftRange: '—',
      monthlyCost: 0
    }
  ];

export const MOCK_CORPORATE_SUBSCRIPTION_INVOICE_GROUPS: CorporateSubscriptionInvoiceGroup[] =
  [
    {
      id: 'grp-1',
      referenceNumber: 'SUB-2026-0801-a1b2c3d4',
      corporationId: 'corp-1',
      corporationName: 'DEV Test Corporate 2',
      status: InvoiceGroupStatus.Paid,
      weekStartDate: '2026-08-01',
      weekEndDate: '2026-08-31',
      groupType: InvoiceGroupType.Subscription,
      hasOverdueInvoices: false,
      createdAt: '2026-09-01',
      invoiceCount: 3,
      totalAmount: 2320,
    },
    {
      id: 'grp-2',
      referenceNumber: 'SUB-2026-0701-a1b2c3d4',
      corporationId: 'corp-1',
      corporationName: 'DEV Test Corporate 2',
      // Mixed paid/failing is the normal steady state for a corporation of any
      // size — Stripe charges each facility separately and failure does not
      // cascade (Specification v2 §10.4).
      status: InvoiceGroupStatus.PartiallyPaid,
      weekStartDate: '2026-07-01',
      weekEndDate: '2026-07-31',
      groupType: InvoiceGroupType.Subscription,
      hasOverdueInvoices: false,
      createdAt: '2026-09-01',
      invoiceCount: 3,
      totalAmount: 2320,
    },
    {
      id: 'grp-3',
      referenceNumber: 'SUB-2026-0601-a1b2c3d4',
      corporationId: 'corp-1',
      corporationName: 'DEV Test Corporate 2',
      status: InvoiceGroupStatus.Overdue,
      weekStartDate: '2026-06-01',
      weekEndDate: '2026-06-30',
      groupType: InvoiceGroupType.Subscription,
      hasOverdueInvoices: false,
      createdAt: '2026-09-01',
      invoiceCount: 3,
      totalAmount: 2320,
    }
  ];

export const MOCK_CORPORATE_SUBSCRIPTION_INVOICE_GROUP_DETAIL: CorporateSubscriptionInvoiceGroupDetail =
  {
    ...MOCK_CORPORATE_SUBSCRIPTION_INVOICE_GROUPS[0],
    // Reconciles with its member invoices below.
    totalAmount: 2250,
    createdAt: '2026-08-31',
    // Shaped as the group route actually answers — `invoiceStatus`, and none
    // of the balance or Stripe fields a full SubscriptionInvoice carries.
    invoices: MOCK_CORPORATE_FACILITY_SUBSCRIPTIONS.map((facility, index) => ({
      id: `grp-1-inv-${index + 1}`,
      invoiceNumber: `INV-2026-01${index + 2}`,
      facilityId: facility.facilityId,
      facilityName: facility.facilityName,
      totalAmount: 750,
      invoiceStatus: 'Paid' as const,
      stripePaymentStatus: null,
      isProcessing: false,
      dueDate: '2026-08-31',
      paidAt: '2026-08-31T00:00:00Z',
      countableShifts: 64,
      finalTierDisplay: 'Tier 3',
      finalTierShiftRange: facility.tierShiftRange
    }))
  };
